/**
 * v4.28 - 2026-10-13 - G10-B (UI-PERMISSION-LIST-NOT-ENFORCED-01): permissions come from the shared /api/me snapshot read once in the layout; this page no longer calls /api/me
 * Webhooks Page - Manage custom webhook endpoints
 *
 * v2.99 - 2026-02-07 - Code review fixes:
 *   #1: Add role-based permission gating (owner/admin only)
 *   #8: Add delete confirmation dialog
 *   #9: Roll back optimistic toggle on failure
 *   #10: Handle missing secret in create response
 *   #17: Add frontend URL format validation (HTTPS required)
 *   #19: Fix timeAgo for future dates (clock skew)
 *   #20: Add loading state during delete
 *   #21: Fix useEffect dependencies
 * v2.98 - 2026-02-07 - Custom webhooks: create, test, view logs, enable/disable
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  PlayArrow as TestIcon,
  History as LogsIcon,
  ContentCopy as CopyIcon,
  Webhook as WebhookIcon,
} from '@mui/icons-material';
import { webhookService, Webhook, WebhookEvent, DeliveryLog } from '../services/api';
import { usePermissions } from '../hooks/usePermissions';

const Webhooks: React.FC = () => {
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // v2.99 - Fix #1: Role-based permission gating
  const { loaded, hasPermission } = usePermissions(); // v4.28 - G10-B: server answer, read once in the layout
  const canManageWebhooks = hasPermission('manageWebhooks');

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createUrl, setCreateUrl] = useState('');
  const [createUrlError, setCreateUrlError] = useState<string | null>(null); // v2.99 - Fix #17
  const [createEvents, setCreateEvents] = useState<string[]>([]);
  const [availableEvents, setAvailableEvents] = useState<WebhookEvent[]>([]);
  const [creating, setCreating] = useState(false);
  const [newSecret, setNewSecret] = useState<string | null>(null);

  // v2.99 - Fix #8: Delete confirmation dialog
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null); // v2.99 - Fix #20

  // Logs dialog
  const [logsOpen, setLogsOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [logsWebhookId, setLogsWebhookId] = useState('');
  const [logs, setLogs] = useState<DeliveryLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // Testing
  const [testing, setTesting] = useState<string | null>(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState<string | null>(null);

  // v2.99 - Fix #21: Wrap fetch functions in useCallback
  const fetchWebhooks = useCallback(async () => {
    setLoading(true);
    try {
      const result = await webhookService.getAll();
      setWebhooks(result.webhooks || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load webhooks');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      const result = await webhookService.getEvents();
      setAvailableEvents(result.events || []);
    } catch {
      // Non-critical, use defaults
      setAvailableEvents([
        { event: 'pipeline.complete', description: 'Pipeline completes successfully' },
        { event: 'pipeline.failed', description: 'Pipeline fails' },
        { event: 'sync.error', description: 'Data source sync error' },
        { event: 'kpi.threshold', description: 'KPI crosses threshold' },
        { event: 'team.invite_accepted', description: 'Team invite accepted' },
        { event: 'deletion.scheduled', description: 'Account deletion scheduled' },
      ]);
    }
  }, []);


  // v2.99 - Fix #21: Include fetchWebhooks/fetchEvents in dependency array
  useEffect(() => {
    fetchWebhooks();
    fetchEvents();
  }, [fetchWebhooks, fetchEvents]);

  // v2.99 - Fix #17: Validate URL format
  const validateUrl = (url: string): boolean => {
    setCreateUrlError(null);
    if (!url.trim()) return false;
    try {
      const parsed = new URL(url);
      if (parsed.protocol !== 'https:') {
        setCreateUrlError('URL must use HTTPS');
        return false;
      }
      return true;
    } catch {
      setCreateUrlError('Invalid URL format');
      return false;
    }
  };

  const handleCreate = async () => {
    if (!createName.trim() || !createUrl.trim() || createEvents.length === 0) return;
    if (!validateUrl(createUrl)) return; // v2.99 - Fix #17
    setCreating(true);
    setError(null);
    try {
      const result = await webhookService.create({
        name: createName.trim(),
        url: createUrl.trim(),
        events: createEvents,
      });
      // v2.99 - Fix #10: Only show secret dialog if secret was returned
      const secret = result.webhook?.secret;
      if (secret) {
        setNewSecret(secret);
      } else {
        setSnackbar('Webhook created (signing secret unavailable; recreate if needed)');
      }
      setCreateOpen(false);
      setCreateName('');
      setCreateUrl('');
      setCreateUrlError(null);
      setCreateEvents([]);
      fetchWebhooks();
    } catch (err: any) {
      setError(err.message || 'Failed to create webhook');
    } finally {
      setCreating(false);
    }
  };

  // v2.99 - Fix #8: Delete with confirmation dialog
  const handleDeleteClick = (id: string) => {
    setDeleteTargetId(id);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;
    const id = deleteTargetId;
    setDeleteConfirmOpen(false);
    setDeleteTargetId(null);
    setDeleting(id); // v2.99 - Fix #20: Track delete loading state
    try {
      await webhookService.delete(id);
      setWebhooks((prev) => prev.filter((w) => w.id !== id));
      setSnackbar('Webhook deleted');
    } catch (err: any) {
      setError(err.message || 'Failed to delete webhook');
    } finally {
      setDeleting(null);
    }
  };

  // v2.99 - Fix #9: Move state update after API success (not optimistic)
  const handleToggleActive = async (webhook: Webhook) => {
    try {
      const result = await webhookService.update(webhook.id, { active: !webhook.active });
      // Use backend response to update state (not optimistic)
      setWebhooks((prev) => prev.map((w) => w.id === webhook.id ? {
        ...w,
        active: result.webhook.active,
        failureCount: result.webhook.failureCount,
      } : w));
      setSnackbar(result.webhook.active ? 'Webhook enabled' : 'Webhook disabled');
    } catch (err: any) {
      setError(err.message || 'Failed to update webhook');
    }
  };

  const handleTest = async (id: string) => {
    setTesting(id);
    try {
      const result = await webhookService.test(id);
      setSnackbar(result.success ? `Test delivered (HTTP ${result.statusCode})` : `Test failed: ${result.error}`);
    } catch (err: any) {
      setSnackbar(`Test failed: ${err.message}`);
    } finally {
      setTesting(null);
    }
  };

  const handleViewLogs = async (webhookId: string) => {
    setLogsWebhookId(webhookId);
    setLogsOpen(true);
    setLogsLoading(true);
    try {
      const result = await webhookService.getLogs(webhookId);
      setLogs(result.logs || []);
    } catch {
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleEventToggle = (event: string) => {
    setCreateEvents((prev) =>
      prev.includes(event) ? prev.filter((e) => e !== event) : [...prev, event]
    );
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSnackbar('Copied to clipboard');
  };

  const getStatusChip = (webhook: Webhook) => {
    if (!webhook.active) return <Chip label="Disabled" size="small" color="default" />;
    if (webhook.failureCount >= 5) return <Chip label="Failing" size="small" color="warning" />;
    return <Chip label="Active" size="small" color="success" />;
  };

  // v2.99 - Fix #19: Handle future dates (clock skew)
  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    if (diff < 0) return 'just now'; // v2.99 - Guard against clock skew
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  // v2.99 - Fix #1: Permission check — show read-only for non-admin/owner
  if (loaded && !canManageWebhooks) {
    return (
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>Webhooks</Typography>
        <Alert severity="warning">
          You don't have permission to manage webhooks. Contact your account owner or admin.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Webhooks</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Add Webhook
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : webhooks.length === 0 ? (
        <Alert severity="info">
          No webhooks configured. Click "Add Webhook" to receive notifications when events occur in your account.
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {webhooks.map((webhook) => (
            <Card key={webhook.id} variant="outlined">
              <CardContent sx={{ pb: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <WebhookIcon color="primary" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>{webhook.name}</Typography>
                  {getStatusChip(webhook)}
                  <Switch
                    checked={webhook.active}
                    onChange={() => handleToggleActive(webhook)}
                    size="small"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{webhook.url}</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                  {webhook.events.map((e) => (
                    <Chip key={e} label={e} size="small" variant="outlined" />
                  ))}
                </Box>
                <Typography variant="caption" color="text.disabled">
                  Created {new Date(webhook.createdAt).toLocaleDateString()}
                  {webhook.lastTriggeredAt && ` | Last triggered ${timeAgo(webhook.lastTriggeredAt)}`}
                  {webhook.failureCount > 0 && ` | ${webhook.failureCount} consecutive failures`}
                </Typography>
              </CardContent>
              <CardActions>
                <Tooltip title="Send test payload">
                  <span>
                    <Button
                      size="small"
                      startIcon={testing === webhook.id ? <CircularProgress size={16} /> : <TestIcon />}
                      onClick={() => handleTest(webhook.id)}
                      disabled={testing === webhook.id}
                    >
                      Test
                    </Button>
                  </span>
                </Tooltip>
                <Button size="small" startIcon={<LogsIcon />} onClick={() => handleViewLogs(webhook.id)}>
                  Logs
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                {/* v2.99 - Fix #20: Disable button during delete */}
                <Tooltip title="Delete webhook">
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteClick(webhook.id)}
                      disabled={deleting === webhook.id}
                    >
                      {deleting === webhook.id ? <CircularProgress size={18} /> : <DeleteIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              </CardActions>
            </Card>
          ))}
        </Box>
      )}

      {/* Create Webhook Dialog */}
      <Dialog open={createOpen} onClose={() => { setCreateOpen(false); setCreateUrlError(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Add Webhook</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
            placeholder="e.g., Slack Notification"
          />
          {/* v2.99 - Fix #17: URL validation with error display */}
          <TextField
            fullWidth
            label="URL"
            value={createUrl}
            onChange={(e) => { setCreateUrl(e.target.value); setCreateUrlError(null); }}
            onBlur={() => { if (createUrl.trim()) validateUrl(createUrl); }}
            error={!!createUrlError}
            helperText={createUrlError || 'Must be a valid HTTPS URL'}
            sx={{ mb: 2 }}
            placeholder="https://hooks.slack.com/..."
          />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Events</Typography>
          <FormGroup>
            {availableEvents.map((ev) => (
              <FormControlLabel
                key={ev.event}
                control={
                  <Checkbox
                    checked={createEvents.includes(ev.event)}
                    onChange={() => handleEventToggle(ev.event)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">{ev.event}</Typography>
                    <Typography variant="caption" color="text.secondary">{ev.description}</Typography>
                  </Box>
                }
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateOpen(false); setCreateUrlError(null); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating || !createName.trim() || !createUrl.trim() || createEvents.length === 0 || !!createUrlError}
          >
            {creating ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Secret Display Dialog — v2.99 - Fix #10: disableEscapeKeyDown to prevent accidental dismiss */}
      <Dialog
        open={newSecret !== null}
        onClose={() => setNewSecret(null)}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>Webhook Created</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Copy your signing secret now. It won't be shown again.
          </Alert>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'grey.100', p: 1.5, borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1, wordBreak: 'break-all' }}>
              {newSecret}
            </Typography>
            <IconButton size="small" onClick={() => copyToClipboard(newSecret || '')}>
              <CopyIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setNewSecret(null)}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* v2.99 - Fix #8: Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Webhook?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete the webhook and its signing secret. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirm}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Delivery Logs Dialog */}
      <Dialog open={logsOpen} onClose={() => setLogsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Delivery Logs</DialogTitle>
        <DialogContent>
          {logsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : logs.length === 0 ? (
            <Alert severity="info">No delivery logs yet.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Event</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>HTTP Code</TableCell>
                    <TableCell>Error</TableCell>
                    <TableCell>Time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell><Chip label={log.event} size="small" variant="outlined" /></TableCell>
                      <TableCell>
                        <Chip
                          label={log.success ? 'OK' : 'Failed'}
                          size="small"
                          color={log.success ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell>{log.statusCode || '-'}</TableCell>
                      <TableCell>
                        <Typography variant="caption" color="error" noWrap sx={{ maxWidth: 200, display: 'block' }}>
                          {log.error || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{timeAgo(log.timestamp)}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar !== null}
        autoHideDuration={3000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
      />
    </Box>
  );
};

export default Webhooks;
